import React, { useState, useEffect } from 'react';
import './TiffinTheme.css';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import tiffinOrderService from '../services/tiffinOrderService';
import whatsAppService from '../../../services/whatsAppService';
import {
  resetTiffin,
} from '../../../redux/slices/tiffinSlice';

const WHATSAPP_ADMIN = process.env.REACT_APP_WHATSAPP_ADMIN || '12038877422';
const PICKUP_ADDRESS = process.env.REACT_APP_PICKUP_ADDRESS || 'Yugal Kunj - Radha Krishna Temple and Community Center, 2769 Duluth Hwy, Duluth, GA 30096';

const TiffinPayment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { order, cart, customerInfo } = useSelector((state) => state.tiffin);
  const { user } = useSelector((state) => state.auth);

  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [processing, setProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successStep, setSuccessStep] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
  const [lookingUpBank, setLookingUpBank] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [savedAdminUrl, setSavedAdminUrl] = useState('');
  const [savedUserUrl, setSavedUserUrl] = useState('');
  const [emailError, setEmailError] = useState(false);

  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.trim(),
    zipCode: user?.User_Zipcode || user?.user_Zipcode || '',
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking',
    bankName: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Redirect if no order data
  useEffect(() => {
    if (!order || !order.tiffinOrder_Id) {
      navigate('/tiffin/menu');
    }
  }, [order, navigate]);

  // Auto-close success modal after 20 seconds
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        dispatch(resetTiffin());
        navigate('/tiffin/success', { state: { paymentResult: { ...paymentResult, mealDate: order?.mealDate }, orderId: order?.tiffinOrder_Id } });
      }, 20000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal, dispatch, navigate, paymentResult, order]);

  // Auto-send email when payment succeeds
  useEffect(() => {
    if (successStep === 'actions') {
      handleSendEmail();
    }
  }, [successStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load and render Google reCAPTCHA v2
  useEffect(() => {
    const ensureScript = () => {
      return new Promise((resolve) => {
        const w = window;
        if (w.grecaptcha) return resolve();
        const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (existing) {
          existing.addEventListener('load', () => resolve());
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });
    };

    const renderWidget = () => {
      const w = window;
      const el = document.getElementById('recaptcha-container-tiffin-payment');
      if (!el) return;
      if (recaptchaWidgetId !== null) return;

      const pollUntilReady = () => {
        const api = (w && w.grecaptcha) ? w.grecaptcha : null;
        if (!api) {
          setTimeout(pollUntilReady, 150);
          return;
        }
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') {
            setTimeout(doRender, 150);
            return;
          }
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (token) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback': () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch (e) {
            console.warn('reCAPTCHA render failed, retrying...', e);
            setTimeout(doRender, 200);
          }
        };

        if (typeof api.ready === 'function') {
          api.ready(doRender);
        } else {
          setTimeout(doRender, 0);
        }
      };

      pollUntilReady();
    };

    let mounted = true;
    ensureScript().then(() => {
      if (!mounted) return;
      setTimeout(renderWidget, 0);
    });
    return () => { mounted = false; };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'cardNumber') {
      const digits = value.replace(/\D/g, '');
      formattedValue = digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
    }
    if (name === 'expiryDate') {
      const digits = value.replace(/\D/g, '');
      formattedValue = digits.length <= 2 ? digits : digits.slice(0, 2) + '/' + digits.slice(2, 4);
    }
    if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '');
    }
    if (name === 'routingNumber') {
      formattedValue = value.replace(/\D/g, '').slice(0, 9);
    }
    if (name === 'accountNumber') {
      formattedValue = value.replace(/\D/g, '');
    }
    if (name === 'zipCode') {
      formattedValue = value.replace(/\D/g, '').slice(0, 5);
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }));

    if (name === 'routingNumber' && formattedValue.length === 9) {
      lookupBankName(formattedValue);
    }

    if (validationErrors[name]) {
      setValidationErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    }
  };

  const lookupBankName = async (routingNumber) => {
    setLookingUpBank(true);
    try {
      const bankName = await tiffinOrderService.lookupBankName(routingNumber);
      if (bankName && bankName.trim().length > 0) {
        setFormData(prev => ({ ...prev, bankName: bankName.trim() }));
      }
    } catch (e) {
      console.error('Bank lookup error:', e);
    } finally {
      setLookingUpBank(false);
    }
  };

  const validateCreditCard = () => {
    const errors = {};
    const cardDigits = formData.cardNumber.replace(/\D/g, '');
    if (!cardDigits) errors.cardNumber = 'Card number is required';
    else if (cardDigits.length < 13 || cardDigits.length > 19) errors.cardNumber = 'Card number must be 13-19 digits';

    if (!formData.expiryDate) {
      errors.expiryDate = 'Expiry date is required';
    } else {
      const m = formData.expiryDate.match(/^(\d{2})\/(\d{2})$/);
      if (!m) errors.expiryDate = 'Use MM/YY format';
      else {
        const month = parseInt(m[1], 10), year = parseInt(m[2], 10);
        if (month < 1 || month > 12) errors.expiryDate = 'Invalid month (01-12)';
        else {
          const now = new Date(), cy = now.getFullYear() % 100, cm = now.getMonth() + 1;
          if (year < cy || (year === cy && month < cm)) errors.expiryDate = 'Card has expired';
        }
      }
    }

    if (!formData.cvv) errors.cvv = 'CVV is required';
    else if (formData.cvv.length < 3 || formData.cvv.length > 4) errors.cvv = 'CVV must be 3-4 digits';

    if (!formData.zipCode || !/^\d{5}$/.test(formData.zipCode)) errors.zipCode = 'Valid 5-digit US ZIP code is required';

    return errors;
  };

  const validateACH = () => {
    const errors = {};
    if (!formData.accountHolderName || formData.accountHolderName.trim().length < 2) errors.accountHolderName = 'Account holder name is required';
    if (!formData.routingNumber) errors.routingNumber = 'Routing number is required';
    else if (formData.routingNumber.length !== 9) errors.routingNumber = 'Routing number must be exactly 9 digits';
    if (!formData.accountNumber) errors.accountNumber = 'Account number is required';
    else if (formData.accountNumber.length < 5 || formData.accountNumber.length > 17) errors.accountNumber = 'Account number must be 5-17 digits';
    if (!formData.bankName || formData.bankName.trim().length < 2) errors.bankName = 'Bank name is required';
    if (!formData.zipCode || !/^\d{5}$/.test(formData.zipCode)) errors.zipCode = 'Valid 5-digit US ZIP code is required';
    return errors;
  };

  const handleSendEmail = async () => {
    if (emailSending || emailSent || !paymentResult) return;
    setEmailSending(true);
    try {
      const cartItemsLocal = Object.values(cart);
      const itemsSummaryLocal = cartItemsLocal.map(c =>
        `${c.item.itemName} x${c.quantity} @ $${(c.item.price || 0).toFixed(2)} = $${((c.item.price || 0) * c.quantity).toFixed(2)}`
      ).join('<br/>');
      const cartTotal = cartItemsLocal.reduce((sum, c) => sum + (c.item.price || 0) * c.quantity, 0);
      const pickupDateForEmail = order?.mealDate
        ? new Date(order.mealDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
        : new Date().toLocaleDateString();
      const resp = await tiffinOrderService.sendConfirmationEmail({
        toEmail: customerInfo.email,
        customerName: `${customerInfo.firstName} ${customerInfo.lastName}`,
        phone: customerInfo.phone,
        totalAmount: paymentResult.amount || cartTotal,
        transactionId: paymentResult.transactionId,
        paymentMethod: paymentResult.paymentMethod,
        notes: customerInfo.notes,
        itemsSummary: itemsSummaryLocal,
        transactionDate: new Date().toLocaleString(),
        pickupTime: customerInfo.pickupTime,
        pickupDate: pickupDateForEmail,
      });
      // Extract receipt URL from response (multiple possible field names)
      let url = '';
      if (typeof resp === 'string' && resp.startsWith('http')) {
        url = resp;
      } else if (resp) {
        url = resp?.receiptUrl || resp?.ReceiptUrl || resp?.attachment || resp?.Attachment ||
              resp?.attachmentUrl || resp?.AttachmentUrl || resp?.pdfUrl || resp?.fileUrl ||
              resp?.url || resp?.Url || resp?.Response || resp?.response ||
              resp?.data?.receiptUrl || resp?.data?.attachment || resp?.data?.url ||
              resp?.data?.Response || resp?.data?.response || '';
      }
      setReceiptUrl(url);
      setEmailSent(true);
    } catch (e) {
      console.warn('Email send error:', e);
      setEmailError(true);
    } finally {
      setEmailSending(false);
    }
  };

  const handleDone = () => {
    const successState = {
      paymentResult: {
        ...paymentResult,
        pickupTime: customerInfo.pickupTime,
        pickupAddress: PICKUP_ADDRESS,
        pickupPerson: `${customerInfo.firstName} ${customerInfo.lastName}`,
        mealDate: order?.mealDate,
      },
      orderId: paymentResult?.orderId,
    };
    sessionStorage.setItem('tiffinSuccessState', JSON.stringify(successState));
    dispatch(resetTiffin());
    navigate('/tiffin/success', { state: successState });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recaptchaToken) {
      setValidationErrors({ recaptcha: 'Please complete the reCAPTCHA verification' });
      document.getElementById('recaptcha-container-tiffin-payment')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    let errors = paymentMethod === 'credit' ? validateCreditCard() : validateACH();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const firstField = Object.keys(errors)[0];
      document.getElementsByName(firstField)[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setProcessing(true);

    try {
      // Build payment details
      const paymentDetails = {
        amount: order.totalAmount,
        orderId: order.tiffinOrder_Id,
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        email: customerInfo.email,
        phone: customerInfo.phone,
        userId: user?.User_Id || user?.user_Id || user?.userId,
        streetAddress1: user?.User_Address_Line1 || user?.user_Address_Line1 || '-',
        streetAddress2: user?.User_Address_Line2 || user?.user_Address_Line2 || '-',
        city: user?.User_City || user?.user_City || '-',
        stateCode: user?.StateSortCode || user?.stateSortCode || 'GA',
        zipCode: formData.zipCode || '-',
        country: user?.CountrySortCode || user?.countrySortCode || 'US',
        paymentType: paymentMethod === 'credit' ? 'CreditCard' : 'ACH',
      };

      if (paymentMethod === 'credit') {
        paymentDetails.cardNumber = formData.cardNumber.replace(/\s/g, '');
        paymentDetails.expiryDate = formData.expiryDate;
        paymentDetails.cvv = formData.cvv;
      } else {
        paymentDetails.accountHolderName = formData.accountHolderName;
        paymentDetails.routingNumber = formData.routingNumber;
        paymentDetails.accountNumber = formData.accountNumber;
        paymentDetails.accountType = formData.accountType;
        paymentDetails.bankName = formData.bankName || 'Financial Institution';
      }

      // Process payment via AuthNet
      const paymentResponse = await tiffinOrderService.processPayment(paymentDetails);
      console.log('Payment Response:', paymentResponse);

      const isApproved = paymentResponse?.createTransactionResponse?.transactionResponse?.responseCode === "1" ||
                         paymentResponse?.createTransactionResponse?.messages?.resultCode === "Ok";

      if (paymentResponse && isApproved) {
        const transactionId = paymentResponse?.createTransactionResponse?.transactionResponse?.transId || '';

        // Confirm payment in our backend — route by order type
        if (order.orderType === 'kitchen') {
          await tiffinOrderService.confirmKitchenPayment({
            kitchen_Orders_Id: order.tiffinOrder_Id,
            userId: customerInfo?.userId || 0,
            paymentStatus: 'Paid',
            paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
            transactionId,
          });
        } else {
          await tiffinOrderService.confirmPayment({
            tiffinOrder_Id: order.tiffinOrder_Id,
            paymentStatus: 'Paid',
            paymentId: transactionId,
            paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
            transactionId,
          });
        }

        // Build items summary (used by manual Send Email button later)
        const cartItemsList2 = Object.values(cart);
        const itemsSummary = cartItemsList2.map(c => `${c.item.itemName} x${c.quantity}`).join(', ');

        const result = {
          transactionId,
          amount: order.totalAmount,
          paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          orderId: order.tiffinOrder_Id,
        };
        setPaymentResult(result);

        // Build WhatsApp URLs
        const cartItemsList = Object.values(cart);
        const itemsText = cartItemsList.map(c => `  - ${c.item.itemName} x${c.quantity} ($${(c.item.price * c.quantity).toFixed(2)})`).join('\n');
        let userPhone = (customerInfo.phone || '').replace(/\D/g, '');
        if (userPhone.length === 10) userPhone = '1' + userPhone;

        const adminMsg = [
          `*New Tiffin Order #${order.tiffinOrder_Id}*`,
          ``,
          `*Name:* ${customerInfo.firstName} ${customerInfo.lastName}`,
          `*Phone:* ${customerInfo.phone || 'N/A'}`,
          `*Email:* ${customerInfo.email}`,
          ``,
          `*Items:*`,
          itemsText,
          ``,
          `*Total:* $${order.totalAmount?.toFixed(2)}`,
          `*Payment:* ${paymentMethod === 'credit' ? 'Credit Card' : 'ACH'}`,
          `*Transaction:* ${transactionId}`,
          ``,
          `*Pickup Time:* ${customerInfo.pickupTime || 'N/A'}`,
          `*Pickup Person:* ${customerInfo.firstName} ${customerInfo.lastName}`,
          `*Pickup Location:* ${PICKUP_ADDRESS}`,
        ].join('\n');

        const userMsg = [
          `*Your Tiffin Order Receipt*`,
          ``,
          `Order #${order.tiffinOrder_Id}`,
          ``,
          `*Items:*`,
          itemsText,
          ``,
          `*Total:* $${order.totalAmount?.toFixed(2)}`,
          `*Payment:* ${paymentMethod === 'credit' ? 'Credit Card' : 'ACH'}`,
          `*Transaction:* ${transactionId}`,
          ``,
          `*Pickup Time:* ${customerInfo.pickupTime || 'N/A'}`,
          `*Pickup Person:* ${customerInfo.firstName} ${customerInfo.lastName}`,
          `*Pickup Location:* ${PICKUP_ADDRESS}`,
          ``,
          `Thank you for your order! \u{1F64F}`,
        ].join('\n');

        const adminUrl = `https://wa.me/${WHATSAPP_ADMIN}?text=${encodeURIComponent(adminMsg)}`;
        const userUrl = userPhone ? `https://wa.me/${userPhone}?text=${encodeURIComponent(userMsg)}` : null;

        setSavedAdminUrl(adminUrl);
        setSavedUserUrl(userUrl || '');
        setSuccessStep('actions');

        // WhatsApp payment confirmation via Meta Cloud API (fire-and-forget)
        // Backend fetches coordinator phones from App_Email_Configuration.Mobile_CoOrdinator
        try {
          let waPhone = (customerInfo.phone || '').replace(/\D/g, '');
          if (waPhone.length === 10) waPhone = '1' + waPhone;
          const phoneList = waPhone ? [waPhone] : [];
          console.log('[WhatsApp] Tiffin notification — coordinators from DB + user phone:', phoneList, 'order:', order.tiffinOrder_Id);
          whatsAppService.sendPaymentConfirmation({
            customerName: `${customerInfo.firstName} ${customerInfo.lastName}`,
            phoneNumbers: phoneList,
            paymentId: transactionId,
            amount: String(order.totalAmount?.toFixed(2) || '0.00'),
            currency: 'USD',
            orderId: String(order.tiffinOrder_Id),
            serviceName: 'Tiffin',
            appName: 'Tiffin',
            templateName: 'YKTiffinPayment',
            orgId: 3,
            orderItems: itemsSummary,
            pickupTime: customerInfo.pickupTime || 'N/A'
          })
            .then(r => console.log('[WhatsApp] Tiffin sent:', r))
            .catch(e => console.warn('[WhatsApp] Tiffin notification failed:', e));
        } catch (waErr) {
          console.warn('[WhatsApp] Tiffin notification error:', waErr);
        }

      } else {
        const errMsg = paymentResponse?.createTransactionResponse?.transactionResponse?.errors?.[0]?.errorText
          || paymentResponse?.meta?.errors?.errorMessages?.[0]?.message
          || 'Payment was declined. Please check your details and try again.';
        setErrorMessage(errMsg);
        setShowErrorModal(true);
      }

    } catch (err) {
      console.error('Payment error:', err);
      const apiData = err?.response?.data;
      const errMsg =
        apiData?.meta?.errors?.errorMessages?.[0]?.message ||
        (apiData?.meta?.errors?.errorCode ? `Payment error: ${apiData.meta.errors.errorCode}` : null) ||
        apiData?.message ||
        err?.message ||
        'An error occurred while processing payment. Please try again.';
      setErrorMessage(errMsg);
      setShowErrorModal(true);
    } finally {
      setProcessing(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    dispatch(resetTiffin());
    navigate('/tiffin/menu');
  };

  if (!order) return null;

  const cartItems = Object.values(cart);

  return (
    <div className="tiffin-payment-page min-h-screen" style={{background: 'var(--yk-bg, linear-gradient(180deg, #faf8f5 0%, #f5f0eb 40%, #faf8f5 100%))'}}>
      {/* Header */}
      <section className="relative" style={{background: 'var(--yk-surface, linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(250,245,238,0.9) 100%))', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid var(--yk-border, rgba(180,160,130,0.15))', boxShadow: '0 1px 12px rgba(120,80,40,0.04))'}}>
        <div className="container-custom py-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/tiffin/menu')} className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105" style={{background: 'var(--yk-surface, rgba(255,255,255,0.9))', border: '1px solid var(--yk-border, rgba(180,160,130,0.3))', boxShadow: '0 2px 8px rgba(0,0,0,0.12)'}}>
              <svg className="w-5 h-5" style={{color: 'var(--yk-text-muted, #78716c)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{color: 'var(--yk-text, #44403c)', letterSpacing: '-0.02em'}}>Payment</h1>
              <p className="text-sm mt-1" style={{color: 'var(--yk-text-muted, #a8a29e)'}}>Complete your tiffin order</p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{background: 'linear-gradient(90deg, transparent 0%, rgba(180,140,80,0.15) 50%, transparent 100%)'}}></div>
      </section>

      <div className="container-custom py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-16">

          {/* Payment Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              {/* Payment Method Toggle */}
              <div className="tiffin-payment-card rounded-2xl p-6 mb-6" style={{background: 'var(--yk-surface, rgba(255,255,255,0.8))', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: '0 2px 12px rgba(120,80,40,0.04), inset 0 1px 0 rgba(255,255,255,0.6)'}}>
                <h2 className="text-lg font-bold mb-4" style={{color: 'var(--yk-text, #44403c)'}}>Payment Method</h2>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button"
                    onClick={() => { setPaymentMethod('credit'); setValidationErrors({}); }}
                    className={`p-4 rounded-xl transition-all duration-200 text-center hover:scale-[1.02] tiffin-payment-tile${paymentMethod === 'credit' ? ' is-active' : ''}`}
                    style={{background: paymentMethod === 'credit' ? 'var(--yk-surface-2, linear-gradient(135deg, #fdf6ee 0%, #f5e6d0 100%))' : 'var(--yk-surface, rgba(255,255,255,0.6))', border: paymentMethod === 'credit' ? '1px solid var(--yk-border, rgba(200,170,110,0.4))' : '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: paymentMethod === 'credit' ? '0 4px 15px rgba(160,130,70,0.1)' : '0 1px 4px rgba(0,0,0,0.02)'}}
                  >
                    <div className="text-2xl mb-1">💳</div>
                    <p className="font-semibold text-sm" style={{color: paymentMethod === 'credit' ? 'var(--yk-primary, #8b6914)' : 'var(--yk-text-muted, #78716c)'}}>Credit Card</p>
                  </button>
                  <button type="button"
                    onClick={() => { setPaymentMethod('ach'); setValidationErrors({}); }}
                    className={`p-4 rounded-xl transition-all duration-200 text-center hover:scale-[1.02] tiffin-payment-tile${paymentMethod === 'ach' ? ' is-active' : ''}`}
                    style={{background: paymentMethod === 'ach' ? 'var(--yk-surface-2, linear-gradient(135deg, #fdf6ee 0%, #f5e6d0 100%))' : 'var(--yk-surface, rgba(255,255,255,0.6))', border: paymentMethod === 'ach' ? '1px solid var(--yk-border, rgba(200,170,110,0.4))' : '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: paymentMethod === 'ach' ? '0 4px 15px rgba(160,130,70,0.1)' : '0 1px 4px rgba(0,0,0,0.02)'}}
                  >
                    <div className="text-2xl mb-1">🏦</div>
                    <p className="font-semibold text-sm" style={{color: paymentMethod === 'ach' ? 'var(--yk-primary, #8b6914)' : 'var(--yk-text-muted, #78716c)'}}>Bank Account (ACH)</p>
                  </button>
                </div>
              </div>

              {/* Credit Card Fields */}
              {paymentMethod === 'credit' && (
                <div className="tiffin-payment-card rounded-2xl p-6 mb-6" style={{background: 'var(--yk-surface, rgba(255,255,255,0.8))', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: '0 2px 12px rgba(120,80,40,0.04), inset 0 1px 0 rgba(255,255,255,0.6)'}}>
                  <h2 className="text-lg font-bold mb-4" style={{color: 'var(--yk-text, #44403c)'}}>Credit Card Details</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>Cardholder Name</label>
                      <input type="text" name="cardholderName" value={formData.cardholderName} onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: '1px solid var(--yk-border, rgba(180,160,130,0.2))', color: 'var(--yk-text, #292524)'}} placeholder="Name on card" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>Card Number *</label>
                      <input type="text" name="cardNumber" value={formData.cardNumber} onChange={handleInputChange} maxLength={23}
                        className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: validationErrors.cardNumber ? '1px solid #fca5a5' : '1px solid rgba(180,160,130,0.2)', color: 'var(--yk-text, #292524)'}} placeholder="1234 5678 9012 3456" />
                      {validationErrors.cardNumber && <p className="text-red-500 text-xs mt-1">{validationErrors.cardNumber}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>Billing ZIP Code *{user ? <span className="font-normal ml-1" style={{color:'var(--yk-text-muted,#a8a29e)'}}>(from your profile)</span> : ''}</label>
                      <input type="text" name="zipCode" value={formData.zipCode} onChange={handleInputChange} maxLength={5}
                        className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: validationErrors.zipCode ? '1px solid #fca5a5' : '1px solid rgba(180,160,130,0.2)', color: 'var(--yk-text, #292524)'}} placeholder="12345" readOnly={!!user} />
                      {validationErrors.zipCode && <p className="text-red-500 text-xs mt-1">{validationErrors.zipCode}</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>Expiry Date *</label>
                        <input type="text" name="expiryDate" value={formData.expiryDate} onChange={handleInputChange} maxLength={5}
                          className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: validationErrors.expiryDate ? '1px solid #fca5a5' : '1px solid rgba(180,160,130,0.2)', color: 'var(--yk-text, #292524)'}} placeholder="MM/YY" />
                        {validationErrors.expiryDate && <p className="text-red-500 text-xs mt-1">{validationErrors.expiryDate}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>CVV *</label>
                        <input type="text" name="cvv" value={formData.cvv} onChange={handleInputChange} maxLength={4}
                          className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: validationErrors.cvv ? '1px solid #fca5a5' : '1px solid rgba(180,160,130,0.2)', color: 'var(--yk-text, #292524)'}} placeholder="123" />
                        {validationErrors.cvv && <p className="text-red-500 text-xs mt-1">{validationErrors.cvv}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ACH Fields */}
              {paymentMethod === 'ach' && (
                <div className="rounded-2xl p-6 mb-6" style={{background: 'var(--yk-surface, rgba(255,255,255,0.8))', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: '0 2px 12px rgba(120,80,40,0.04), inset 0 1px 0 rgba(255,255,255,0.6)'}}>
                  <h2 className="text-lg font-bold mb-4" style={{color: 'var(--yk-text, #44403c)'}}>Bank Account Details</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>Account Holder Name *</label>
                      <input type="text" name="accountHolderName" value={formData.accountHolderName} onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: validationErrors.accountHolderName ? '1px solid #fca5a5' : '1px solid rgba(180,160,130,0.2)', color: 'var(--yk-text, #292524)'}} placeholder="Account holder name" />
                      {validationErrors.accountHolderName && <p className="text-red-500 text-xs mt-1">{validationErrors.accountHolderName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>Account Type *</label>
                      <select name="accountType" value={formData.accountType} onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: '1px solid rgba(180,160,130,0.2)', color: 'var(--yk-text, #292524)'}}>
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>Routing Number *</label>
                      <input type="text" name="routingNumber" value={formData.routingNumber} onChange={handleInputChange} maxLength={9}
                        className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: validationErrors.routingNumber ? '1px solid #fca5a5' : '1px solid rgba(180,160,130,0.2)', color: 'var(--yk-text, #292524)'}} placeholder="9 digit routing number" />
                      {validationErrors.routingNumber && <p className="text-red-500 text-xs mt-1">{validationErrors.routingNumber}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>Account Number *</label>
                      <input type="text" name="accountNumber" value={formData.accountNumber} onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: validationErrors.accountNumber ? '1px solid #fca5a5' : '1px solid rgba(180,160,130,0.2)', color: 'var(--yk-text, #292524)'}} placeholder="Account number" />
                      {validationErrors.accountNumber && <p className="text-red-500 text-xs mt-1">{validationErrors.accountNumber}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>Billing ZIP Code *{user ? <span className="font-normal ml-1" style={{color:'var(--yk-text-muted,#a8a29e)'}}>(from your profile)</span> : ''}</label>
                      <input type="text" name="zipCode" value={formData.zipCode} onChange={handleInputChange} maxLength={5}
                        className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: validationErrors.zipCode ? '1px solid #fca5a5' : '1px solid rgba(180,160,130,0.2)', color: 'var(--yk-text, #292524)'}} placeholder="12345" readOnly={!!user} />
                      {validationErrors.zipCode && <p className="text-red-500 text-xs mt-1">{validationErrors.zipCode}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1.5" style={{color: 'var(--yk-text-muted, #57534e)'}}>Bank Name *</label>
                      <div className="relative">
                        <input type="text" name="bankName" value={formData.bankName} onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-xl outline-none transition text-sm" style={{background: 'var(--yk-surface-2, rgba(250,247,243,0.7))', border: validationErrors.bankName ? '1px solid #fca5a5' : '1px solid rgba(180,160,130,0.2)', color: 'var(--yk-text, #292524)'}} placeholder="Bank name" />
                        {lookingUpBank && <span className="absolute right-3 top-3.5 text-xs" style={{color: 'var(--yk-text-muted, #a8a29e)'}}>Looking up...</span>}
                      </div>
                      {validationErrors.bankName && <p className="text-red-500 text-xs mt-1">{validationErrors.bankName}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* reCAPTCHA */}
              <div className="tiffin-payment-card rounded-2xl p-6 mb-6" style={{background: 'var(--yk-surface, rgba(255,255,255,0.8))', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: '0 2px 12px rgba(120,80,40,0.04), inset 0 1px 0 rgba(255,255,255,0.6)'}}>
                <div id="recaptcha-container-tiffin-payment" className="flex justify-center"></div>
                {validationErrors.recaptcha && <p className="text-red-500 text-sm mt-2 text-center">{validationErrors.recaptcha}</p>}
              </div>

              {/* Submit */}
              <button type="submit" disabled={processing}
                className="tiffin-pay-btn w-full font-bold py-4 px-6 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-base tracking-wide hover:scale-[1.02] active:scale-[0.98]"
                style={{background: '#ffffff', color: '#111827', border: '1.5px solid #d1d5db', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}
              >
                {processing ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Processing Payment...
                  </span>
                ) : (
                  `Pay $${order.totalAmount?.toFixed(2) || '0.00'}`
                )}
              </button>
            </form>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1" style={{borderLeft: '0', paddingLeft: '0'}}>
            <div className="sticky top-6">
              <div className="rounded-2xl overflow-hidden" style={{background: 'var(--yk-surface, rgba(255,255,255,0.8))', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: '0 10px 40px rgba(120,80,40,0.07), 0 2px 6px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.5)'}}>
                <div className="px-6 py-5" style={{background: 'var(--yk-surface-2, linear-gradient(135deg, #fdf6ee 0%, #f5e6d0 100%))', borderBottom: '1px solid var(--yk-border, rgba(200,170,110,0.2))'}}>
                  <h2 className="text-lg font-bold" style={{color: 'var(--yk-text, #6b4c0a)'}}>Order Summary</h2>
                  <p className="text-sm mt-0.5" style={{color: 'var(--yk-text-muted, #a8946a)'}}>Order #{order.tiffinOrder_Id}</p>
                </div>
                <div className="p-6">
                  {/* Items */}
                  <div className="space-y-3 mb-4">
                    {cartItems.map((c) => (
                      <div key={c.item.tiffinMenuItem_Id} className="flex items-center justify-between text-sm">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate" style={{color: 'var(--yk-text, #292524)'}}>{c.item.itemName}</p>
                          <p style={{color: 'var(--yk-text-muted, #a8a29e)'}}>${(c.item.price || 0).toFixed(2)} × {c.quantity}</p>
                        </div>
                        <p className="font-bold ml-3" style={{color: 'var(--yk-text, #292524)'}}>${((c.item.price || 0) * c.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>

                  {/* Meal date + pickup time for Sita Rasoi orders */}
                  {order?.orderType === 'kitchen' && order?.mealDate && (
                    <div className="rounded-xl px-4 py-3 mb-4 text-sm" style={{background: 'var(--yk-surface-2, linear-gradient(135deg,#fdf6ee,#f5e6d0))', border: '1px solid var(--yk-border, rgba(200,170,110,0.3))'}}>
                      <p className="mb-1" style={{color: 'var(--yk-text-muted, #57534e)'}}>
                        <span className="font-semibold" style={{color: 'var(--yk-text, #44403c)'}}>📅 Meal Date:</span>{' '}
                        {new Date(order.mealDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                      {customerInfo.pickupTime && (
                        <p style={{color: 'var(--yk-text-muted, #57534e)'}}>
                          <span className="font-semibold" style={{color: 'var(--yk-text, #44403c)'}}>⏰ Pickup:</span>{' '}
                          {customerInfo.pickupTime}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Customer Info */}
                  <div className="pt-4 mb-4" style={{borderTop: '1px solid rgba(180,160,130,0.1)'}}>
                    <p className="text-sm mb-1" style={{color: 'var(--yk-text-muted, #57534e)'}}><span className="font-semibold" style={{color: 'var(--yk-text, #44403c)'}}>Name:</span> {customerInfo.firstName} {customerInfo.lastName}</p>
                    <p className="text-sm mb-1" style={{color: 'var(--yk-text-muted, #57534e)'}}><span className="font-semibold" style={{color: 'var(--yk-text, #44403c)'}}>Email:</span> {customerInfo.email}</p>
                    {customerInfo.phone && <p className="text-sm" style={{color: 'var(--yk-text-muted, #57534e)'}}><span className="font-semibold" style={{color: 'var(--yk-text, #44403c)'}}>Phone:</span> {customerInfo.phone}</p>}
                  </div>

                  {/* Total */}
                  <div className="pt-4" style={{borderTop: '1px solid rgba(180,160,130,0.1)'}}>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold" style={{color: 'var(--yk-text, #44403c)'}}>Total</span>
                      <span className="text-xl font-bold" style={{color: 'var(--yk-primary, #92400e)'}}>${order.totalAmount?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Overlay — shows during actual API call */}
      {processing && !successStep && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)'}}>
          <div className="rounded-2xl p-10 text-center" style={{background: 'var(--yk-surface, rgba(255,255,255,0.95))', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.15))', boxShadow: '0 25px 60px rgba(0,0,0,0.15)'}}>
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full animate-ping" style={{background: 'rgba(107,76,10,0.1)'}}></div>
              <div className="relative w-24 h-24 rounded-full flex items-center justify-center" style={{background: 'var(--yk-surface-2, linear-gradient(135deg, #fdf6ee, #f5e6d0))', border: '2px solid var(--yk-border, rgba(200,170,110,0.4))', boxShadow: '0 8px 30px rgba(0,0,0,0.12)'}}>
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{borderColor: '#c8a96e', borderTopColor: 'transparent'}}></div>
              </div>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{color: 'var(--yk-text, #292524)'}}>Processing Payment...</h2>
            <p className="text-sm" style={{color: 'var(--yk-text-muted, #78716c)'}}>Please wait while we confirm your payment</p>
          </div>
        </div>
      )}

      {/* Success Action Overlay */}
      {successStep === 'actions' && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)'}}>
          <div className="rounded-2xl w-full max-w-sm mx-auto overflow-y-auto" style={{maxHeight: '90vh', background: 'var(--yk-surface, rgba(255,255,255,0.97))', border: '1px solid var(--yk-border, rgba(180,160,130,0.15))', boxShadow: '0 30px 70px rgba(0,0,0,0.18))'}}>

            {/* Header */}
            <div className="px-6 pt-8 pb-4 text-center" style={{borderBottom: '1px solid rgba(180,160,130,0.12)'}}>
              <div className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center" style={{background: 'var(--yk-surface-2, linear-gradient(135deg, #fdf6ee, #f5e6d0))', border: '2px solid var(--yk-border, rgba(200,170,110,0.4))', boxShadow: '0 6px 20px rgba(0,0,0,0.10)'}}>
                <svg className="w-8 h-8" style={{color: 'var(--yk-text, #6b4c0a)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold" style={{color: 'var(--yk-text, #292524)'}}>Payment Successful!</h2>
              {paymentResult && (
                <div className="mt-3 text-sm text-left rounded-xl p-3" style={{background: 'var(--yk-surface-2, linear-gradient(135deg,#fdf6ee,#f5e6d0))', border: '1px solid var(--yk-border, rgba(200,170,110,0.2))'}}>
                  <p style={{color:'var(--yk-text-muted, #57534e)'}}><span className="font-semibold" style={{color:'var(--yk-text, #44403c)'}}>Order:</span> #{paymentResult.orderId}</p>
                  <p style={{color:'var(--yk-text-muted, #57534e)'}}><span className="font-semibold" style={{color:'var(--yk-text, #44403c)'}}>Amount:</span> ${paymentResult.amount?.toFixed(2)}</p>
                  <p style={{color:'var(--yk-text-muted, #57534e)'}}><span className="font-semibold" style={{color:'var(--yk-text, #44403c)'}}>Payment:</span> {paymentResult.paymentMethod}</p>
                  {customerInfo.pickupTime && <p style={{color:'var(--yk-text-muted, #57534e)'}}><span className="font-semibold" style={{color:'var(--yk-text, #44403c)'}}>Pickup:</span> {customerInfo.pickupTime}</p>}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="px-6 py-5 space-y-3">

              {/* Email Status (auto-sent) */}
              {emailError ? (
                <button
                  onClick={() => { setEmailError(false); handleSendEmail(); }}
                  className="w-full flex items-center justify-center gap-2 font-semibold py-3 px-4 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{background: 'linear-gradient(135deg,#fee2e2,#fecaca)', color: '#991b1b', border: '1px solid rgba(239,68,68,0.3)'}}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                  Email failed — Tap to Retry
                </button>
              ) : (
                <div
                  className="tiffin-email-status w-full flex items-center justify-center gap-2 font-semibold py-3 px-4 rounded-xl"
                  style={{background: 'var(--yk-surface-2, rgba(239,246,255,0.8))', color: 'var(--yk-text-muted, #1e40af)', border: '1px solid var(--yk-border, rgba(59,130,246,0.25))'}}
                >
                  {emailSending ? (
                    <><div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{borderColor:'#1e40af',borderTopColor:'transparent'}}></div> Sending email confirmation...</>
                  ) : emailSent ? (
                    <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg> Email confirmation sent!</>
                  ) : (
                    <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg> Preparing email...</>
                  )}
                </div>
              )}

              {/* Download Receipt — shows after email sent if URL available */}
              {receiptUrl ? (
                <a
                  href={receiptUrl} target="_blank" rel="noopener noreferrer"
                  className="tiffin-action-btn w-full flex items-center justify-center gap-2 font-semibold py-3 px-4 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{background: '#ffffff', color: '#111827', border: '1.5px solid #d1d5db', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', textDecoration: 'none', display: 'flex'}}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                  Download Receipt
                </a>
              ) : emailSent && (
                <div className="w-full text-center py-2 text-xs" style={{color:'var(--yk-text-muted, #a8a29e)'}}>Receipt will be attached in your email</div>
              )}

              {/* WhatsApp Admin — hidden per request */}
              {/* WhatsApp User — hidden per request */}

              {/* Done */}
              <button
                onClick={handleDone}
                className="tiffin-action-btn w-full font-bold py-3.5 px-4 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{background: '#ffffff', color: '#111827', border: '1.5px solid #d1d5db', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}
              >
                View Order Confirmation →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)'}}>
          <div className="rounded-2xl max-w-md w-full p-8 text-center" style={{background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(180,160,130,0.15)', boxShadow: '0 25px 60px rgba(0,0,0,0.15), 0 10px 20px rgba(0,0,0,0.05)'}}>
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold mb-2" style={{color: 'var(--yk-text, #292524)'}}>Payment Failed</h2>
            <p className="mb-4 text-sm" style={{color: 'var(--yk-text-muted, #78716c)'}}>{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)}
              className="font-bold py-3 px-8 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{background: 'rgba(250,247,243,0.9)', color: 'var(--yk-text-muted, #57534e)', border: '1px solid rgba(180,160,130,0.25)', boxShadow: '0 4px 14px rgba(120,80,40,0.08), inset 0 1px 0 rgba(255,255,255,0.7)'}}>
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiffinPayment;
